import { Dialog, Transition } from "@headlessui/react";
import { FC, Fragment } from "react";
import { useTranslation } from "next-i18next";

import { classNames } from "@utils/class-names";

interface Props {
  success: boolean;
  open: boolean;
  message: string;
  onSuccessClose(): void;
  onFailureClose(): void;
}

const StatusDialog: FC<Props> = ({
  success,
  open,
  message,
  onFailureClose,
  onSuccessClose,
}) => {
  const { t } = useTranslation("common");

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        onClose={success ? onSuccessClose : onFailureClose}
        className="fixed flex justify-center items-center inset-0 z-30"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-30"
          leave="ease-in duration-200"
          leaveFrom="opacity-30"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-90"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-90"
        >
          <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all bg-white shadow-xl rounded-2xl z-10">
            <Dialog.Title
              as="h3"
              className="text-lg font-medium leading-6 text-gray-900"
            >
              {t(success ? "success" : "error")}
            </Dialog.Title>
            <Dialog.Description as="div" className="mt-4">
              <p className="text-sm text-gray-500">{message}</p>
            </Dialog.Description>

            <button
              onClick={success ? onSuccessClose : onFailureClose}
              className={classNames(
                "inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 border border-transparent rounded-md hover:bg-red-600 focus:outline-none focus-visible:ring-offset-2 focus-visible:ring-gray-700",
                success ? "bg-green-400" : "bg-red-400"
              )}
            >
              OK
            </button>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default StatusDialog;
